"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { UniversalAiModelPicker } from "./universal-ai-model-picker";
import { EnterpriseContextMenu } from "./enterprise-context-menu";
import { publishRunyiValueChange } from "../lib/runyi-value-sync";
import {
  Archive,
  Boxes,
  ChevronDown,
  Database,
  ExternalLink,
  HardDrive,
  LoaderCircle,
  MoreVertical,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UniversalInfrastructureActions, dispatchAddInfrastructure } from "./universal-infrastructure-actions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://enbisficlrovjuqxjksy.supabase.co";
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "sb_publishable_uZhg8SFREduI9flAbRtzqA_kEi1X4Ws";
const fallbackSupabase = createClient(SUPABASE_URL, PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: "runyi:supabase-auth",
  },
});

type ApplicationItem = { entity_key: string; display_name: string; lifecycle_state: string; health_state?: string | null };
type Revision = {
  revision_id: string;
  project_id: string;
  revision: number;
  state: string;
  requirements: unknown[];
  provider_preferences: Record<string, unknown>;
  resource_preferences: Record<string, unknown>;
  policy_constraints: Record<string, unknown>;
  content_hash: string;
  materialized_composition_id?: string | null;
};
type Project = {
  project_id: string;
  project_key: string;
  display_name: string;
  description: string;
  environment_key: string;
  lifecycle_state: string;
  assignment_state: string;
  application_entity_key?: string | null;
  current_revision: number;
  materialized_composition_id?: string | null;
  archived_at?: string | null;
  removed_at?: string | null;
  updated_at: string;
  latest_revision?: Revision | null;
};
type ProviderTruth = {
  binding_id: string;
  provider_key: string;
  provider_account_ref?: string | null;
  provider_project_ref?: string | null;
  provider_account_display: string;
  provider_project_display: string;
  provider_resource_type: string;
  provider_resource_id: string;
  provider_resource_name?: string | null;
  resource_key: string;
  verification_state: string;
  verified_at?: string | null;
  destroy_action_key?: string | null;
  destroy_supported: boolean;
  destroy_action?: {
    risk_level?: string;
    owner_approval_required?: boolean;
    recovery_checkpoint_required?: boolean;
  } | null;
};
type MarketplaceItem = {
  provider_capability_key: string;
  target_entity_key: string;
  provider_key: string;
  display_name: string;
  service_type: string;
  logical_capability_hint?: string | null;
  state: string;
  category_key: string;
};
type CategoryItem = { category_key: string; display_name: string; description: string; sort_order: number };
type ProjectResourcePreference = {
  item_key: string;
  target_entity_key: string;
  display_name?: string;
  category_key: string;
  provider_key?: string | null;
  assignment_role?: string;
  environment_key?: string;
  state?: string;
  target_kind?: string;
};
type Overview = {
  architecture_version: string;
  platform_architecture_version?: string;
  workspace_version?: string;
  role: string;
  projects: Project[];
  applications: ApplicationItem[];
  categories: CategoryItem[];
  marketplace: MarketplaceItem[];
  provider_truth: ProviderTruth[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  supabase?: SupabaseClient | null;
  connected?: boolean;
  initialApplicationEntityKey?: string | null;
  initialProjectId?: string | null;
  initialCreate?: boolean;
  onlyApplicationEntityKey?: string | null;
  onOpenTest?: (project: Project) => void;
  embedded?: boolean;
  externalSearch?: string;
  hideSearch?: boolean;
};


function projectResourceItems(project: Project): ProjectResourcePreference[] {
  const value = project.latest_revision?.resource_preferences?.items;
  return Array.isArray(value) ? value.filter((item): item is ProjectResourcePreference => Boolean(item && typeof item === "object" && "item_key" in item)) : [];
}

function categoryForProviderResource(resourceType: string) {
  const value = String(resourceType || "").toUpperCase();
  if (value.includes("DATABASE") || value === "D1_DATABASE") return "DATABASE";
  if (value.includes("STORAGE") || value.includes("BUCKET") || value === "R2_BUCKET") return "STORAGE";
  if (value.includes("KV")) return "CACHE";
  if (value.includes("QUEUE")) return "QUEUE_MESSAGING";
  if (value.includes("WORKER") || value.includes("RUNTIME")) return "RUNTIME";
  if (value.includes("DNS") || value.includes("EDGE")) return "NETWORK_EDGE";
  return "FUTURE_RESOURCE";
}

function tone(value?: string | null) {
  const state = String(value ?? "").toUpperCase();
  if (["ACTIVE", "ASSIGNED", "MATERIALIZED", "VERIFIED", "READY"].includes(state)) return "bg-emerald-400/10 text-emerald-600 dark:text-emerald-300";
  if (["DRAFT", "UNASSIGNED", "ARCHIVED", "PREPARABLE"].includes(state)) return "bg-amber-400/10 text-amber-700 dark:text-amber-200";
  if (["REMOVED", "FAILED", "BLOCKED"].includes(state)) return "bg-red-400/10 text-red-600 dark:text-red-300";
  return "bg-foreground/[0.06] text-foreground/60";
}

function asJson(value: string, fallback: unknown) {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return JSON.parse(trimmed);
}

async function functionErrorMessage(error: unknown) {
  const fallback = error instanceof Error ? error.message : "Infrastructure Projects request failed.";
  const response = (error as { context?: Response } | null)?.context;
  if (!(response instanceof Response)) return fallback;
  try {
    const payload = await response.clone().json() as Record<string, unknown>;
    const detail = payload.detail ?? payload.message ?? payload.error;
    return detail ? String(detail) : `${fallback} (HTTP ${response.status})`;
  } catch {
    return `${fallback} (HTTP ${response.status})`;
  }
}

export function InfrastructureProjectsDirectory({ open, onClose, supabase: suppliedSupabase, connected = true, initialApplicationEntityKey = null, initialProjectId = null, initialCreate = false, onlyApplicationEntityKey = null, onOpenTest, embedded = false, externalSearch = "", hideSearch = false }: Props) {
  const supabase = suppliedSupabase ?? fallbackSupabase;
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectionContext,setSelectionContext] = useState<{
    target_scope?: "APPLICATION"|"PROJECT"|null;
    target_entity_key?: string|null;
    provider_capability_key?: string|null;
    provider_key?: string|null;
    category_key?: string|null;
    logical_capability?: string|null;
    environment_key?: string|null;
    source_display_name?: string|null;
  } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [assignProject, setAssignProject] = useState<Project | null>(null);
  const [assignApplication, setAssignApplication] = useState<string>(initialApplicationEntityKey ?? "UNASSIGNED");

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [environment, setEnvironment] = useState("PRODUCTION");
  const [application, setApplication] = useState(initialApplicationEntityKey ?? "UNASSIGNED");
  const [requirements, setRequirements] = useState("[]");
  const [providerPreferences, setProviderPreferences] = useState("{}");
  const [resourcePreferences, setResourcePreferences] = useState("{}");
  const [policyConstraints, setPolicyConstraints] = useState("{}");
  const [showAdvancedCreate, setShowAdvancedCreate] = useState(false);
  const [requirementText, setRequirementText] = useState("");
  const [requirementCategory, setRequirementCategory] = useState("ANY");
  const [requirementPriority, setRequirementPriority] = useState("REQUIRED");
  const [resourceChoice, setResourceChoice] = useState("AUTO");
  const [assignmentRole, setAssignmentRole] = useState("PRIMARY");
  const [editResourceTarget, setEditResourceTarget] = useState("");
  const [showProjectTemplate, setShowProjectTemplate] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onBack = (event: Event) => {
      if (event.defaultPrevented) return;
      if (editProject) { event.preventDefault(); setEditProject(null); return; }
      if (assignProject) { event.preventDefault(); setAssignProject(null); return; }
      if (showCreate) { event.preventDefault(); setShowCreate(false); return; }
      if (expanded) { event.preventDefault(); setExpanded(null); return; }
      if (!embedded) { event.preventDefault(); onClose(); }
    };
    window.addEventListener("runyi:request-back", onBack);
    return () => window.removeEventListener("runyi:request-back", onBack);
  }, [open, embedded, editProject, assignProject, showCreate, expanded, onClose]);


  const invoke = useCallback(async (action: string, payload: Record<string, unknown> = {}) => {
    if (!connected) throw new Error("RUNYI session is not connected. Sign in and try again.");
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token ?? "";
    if (sessionError || !accessToken) throw new Error("RUNYI session expired. Sign in again and retry Infrastructure Projects.");

    const { data, error } = await supabase.functions.invoke("runyi-infrastructure-projects", {
      body: { action, ...payload },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (error) throw new Error(await functionErrorMessage(error));
    if (data?.error) throw new Error(String(data.detail ?? data.error));
    if (["create", "save_revision", "assign", "mark_materialized", "archive", "restore", "remove",
      "add_resource_preference", "set_resource_preference_state", "remove_resource_preference"].includes(action)) {
      publishRunyiValueChange(["projects", "applications", "infrastructure", "catalogue"]);
    }
    return data;
  }, [connected, supabase]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoke("overview");
      setOverview(data as Overview);
      setNotice(null);
      return data as Overview;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Infrastructure Projects could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [invoke]);

  useEffect(() => {
    if (!open || !connected) return;
    const timer = window.setTimeout(() => void refresh(), 60);
    const interval = window.setInterval(() => void refresh(), 60_000);
    const onVisibility = () => document.visibilityState === "visible" && void refresh();
    const onFocus = () => void refresh();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [open, refresh]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw=window.sessionStorage.getItem("runyi:selection-context");
      setSelectionContext(raw ? JSON.parse(raw) : null);
    } catch {
      setSelectionContext(null);
    }
  },[open]);

  useEffect(() => {
    if (open && initialCreate) { resetCreate(); setShowCreate(true); }
  }, [open, initialCreate]);

  useEffect(() => {
    if (!initialApplicationEntityKey) return;
    setApplication(initialApplicationEntityKey);
    setAssignApplication(initialApplicationEntityKey);
  }, [initialApplicationEntityKey]);

  useEffect(() => {
    if (!initialProjectId || !overview?.projects.some((item) => item.project_id === initialProjectId)) return;
    setExpanded(initialProjectId);
  }, [initialProjectId, overview]);

  const effectiveSearch = externalSearch.trim() || search.trim();
  const applicationMap = useMemo(() => new Map((overview?.applications ?? []).map((item) => [item.entity_key, item.display_name])), [overview]);
  const filteredMarketplace = useMemo(() => {
    const needle = effectiveSearch.toLowerCase();
    if (!needle) return overview?.marketplace ?? [];
    return (overview?.marketplace ?? []).filter((item) => JSON.stringify(item).toLowerCase().includes(needle));
  }, [effectiveSearch, overview]);

  const filteredProjects = useMemo(() => {
    const needle = effectiveSearch.toLowerCase();
    return (overview?.projects ?? []).filter((project) =>
      (!onlyApplicationEntityKey || project.application_entity_key === onlyApplicationEntityKey) &&
      (!needle || [
      project.project_key,
      project.display_name,
      project.description,
      project.environment_key,
      project.lifecycle_state,
      project.assignment_state,
      project.application_entity_key ?? "",
      applicationMap.get(project.application_entity_key ?? "") ?? "",
    ].join(" ").toLowerCase().includes(needle)));
  }, [applicationMap, effectiveSearch, onlyApplicationEntityKey, overview]);

  const requirementItems = useMemo(() => {
    try {
      const parsed = JSON.parse(requirements);
      if (!Array.isArray(parsed)) return [] as Record<string, unknown>[];
      return parsed
        .map((item) => typeof item === "string" ? { requirement: item } : item)
        .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object"));
    } catch { return [] as Record<string, unknown>[]; }
  }, [requirements]);

  const composerMarketplace = useMemo(() => {
    const items = overview?.marketplace ?? [];
    return requirementCategory === "ANY" ? items : items.filter((item) => item.category_key === requirementCategory);
  }, [overview, requirementCategory]);

  function addStructuredRequirement() {
    const value = requirementText.trim();
    if (!value) return;
    const next = [...requirementItems, {
      requirement: value,
      category_key: requirementCategory === "ANY" ? "FUTURE_RESOURCE" : requirementCategory,
      priority: requirementPriority,
    }];
    setRequirements(JSON.stringify(next, null, 2));
    setRequirementText("");
  }

  function removeStructuredRequirement(index: number) {
    setRequirements(JSON.stringify(requirementItems.filter((_, itemIndex) => itemIndex !== index), null, 2));
  }

  function syncResourcePreference(choice: string, env = environment, role = assignmentRole) {
    setResourceChoice(choice);
    if (choice === "AUTO") return;
    const item = (overview?.marketplace ?? []).find((candidate) => candidate.target_entity_key === choice);
    if (!item) return;
    const current = asJson(resourcePreferences, {}) as { items?: ProjectResourcePreference[] };
    const items = Array.isArray(current.items) ? [...current.items] : [];
    const next: ProjectResourcePreference = {
      item_key: `CREATE:${item.provider_capability_key}`, target_entity_key: item.target_entity_key,
      display_name: item.display_name, category_key: item.category_key, provider_key: item.provider_key,
      assignment_role: role, environment_key: env, state: "DESIRED", target_kind: "PROVIDER_CAPABILITY",
    };
    const index = items.findIndex((entry) => entry.item_key === next.item_key);
    if (index >= 0) items[index] = next;
    else items.push(next);
    setResourcePreferences(JSON.stringify({ ...current, items }, null, 2));
    // A multi-provider plan must not accidentally enforce the last chosen provider globally.
    setProviderPreferences(items.length === 1 ? JSON.stringify({ preferred_provider_key: item.provider_key }, null, 2) : "{}");
  }

  function removeCreateResourcePreference(itemKey: string) {
    const current = asJson(resourcePreferences, {}) as { items?: ProjectResourcePreference[] };
    const items = (Array.isArray(current.items) ? current.items : []).filter((item) => item.item_key !== itemKey);
    setResourcePreferences(JSON.stringify({ ...current, items }, null, 2));
    if (!items.length) setProviderPreferences("{}");
    setResourceChoice("AUTO");
  }

  function resetCreate() {
    setName("");
    setKey("");
    setDescription("");
    setEnvironment("PRODUCTION");
    setApplication(initialApplicationEntityKey ?? "UNASSIGNED");
    setRequirements("[]");
    setProviderPreferences("{}");
    setResourcePreferences("{}");
    setPolicyConstraints("{}");
    setShowAdvancedCreate(false);
    setRequirementText("");
    setRequirementCategory("ANY");
    setRequirementPriority("REQUIRED");
    setResourceChoice("AUTO");
    setAssignmentRole("PRIMARY");
  }

  async function createProject() {
    setBusy("create");
    try {
      await invoke("create", {
        project_key: key,
        display_name: name,
        description,
        environment_key: environment,
        application_entity_key: application === "UNASSIGNED" ? null : application,
        requirements: asJson(requirements, []),
        provider_preferences: asJson(providerPreferences, {}),
        resource_preferences: asJson(resourcePreferences, {}),
        policy_constraints: asJson(policyConstraints, {}),
      });
      setNotice("Infrastructure Project created. No provider resource was changed.");
      setShowCreate(false);
      resetCreate();
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Project creation failed.");
    } finally { setBusy(null); }
  }

  function startEdit(project: Project) {
    const revision = project.latest_revision;
    setEditProject(project);
    setRequirements(JSON.stringify(revision?.requirements ?? [], null, 2));
    setProviderPreferences(JSON.stringify(revision?.provider_preferences ?? {}, null, 2));
    setResourcePreferences(JSON.stringify(revision?.resource_preferences ?? {}, null, 2));
    setPolicyConstraints(JSON.stringify(revision?.policy_constraints ?? {}, null, 2));
    setShowProjectTemplate(false);
    setEditResourceTarget("");
    setRequirementText("");
  }

  function liveEditProject() {
    return overview?.projects.find((project) => project.project_id === editProject?.project_id) ?? editProject;
  }

  function projectTemplate(project: Project) {
    return JSON.stringify({
      schema: "RUNYI_PROJECT_INFRASTRUCTURE_TEMPLATE_V1",
      project: { project_id: project.project_id, project_key: project.project_key, display_name: project.display_name,
        description: project.description, environment_key: project.environment_key,
        application_entity_key: project.application_entity_key, revision: project.current_revision },
      requirements: project.latest_revision?.requirements ?? [],
      resource_assignments: projectResourceItems(project).map((item) => ({
        target_entity_key: item.target_entity_key, display_name: item.display_name,
        provider_key: item.provider_key, category_key: item.category_key,
        environment_key: item.environment_key, assignment_role: item.assignment_role, state: item.state,
      })),
      note: "Versioned product specification for authorized developers and AI. No execution grant is included.",
    }, null, 2);
  }

  async function addEditResourcePreference() {
    const project = liveEditProject();
    const item = (overview?.marketplace ?? []).find((candidate) => candidate.target_entity_key === editResourceTarget);
    if (!project || !item) return;
    const itemKey = `EDITOR:${item.provider_capability_key}:${project.environment_key}:PRIMARY`;
    setBusy(`edit-add:${project.project_id}`);
    try {
      await invoke("add_resource_preference", {
        project_id: project.project_id, item_key: itemKey,
        item: { item_key: itemKey, target_entity_key: item.target_entity_key, display_name: item.display_name,
          category_key: item.category_key, provider_key: item.provider_key, assignment_role: "PRIMARY",
          environment_key: project.environment_key, state: "DESIRED", target_kind: "CAPABILITY",
          logical_capability: item.logical_capability_hint ?? null },
        reason: "Owner added Marketplace capability from project configuration engine.",
      });
      const fresh = await refresh();
      const next = fresh?.projects.find((candidate) => candidate.project_id === project.project_id);
      if (next) {
        setEditProject(next);
        setResourcePreferences(JSON.stringify(next.latest_revision?.resource_preferences ?? {}, null, 2));
      }
      setEditResourceTarget("");
      setNotice("Project configuration updated. No provider infrastructure was provisioned.");
    } catch (error) { setNotice(error instanceof Error ? error.message : "Could not add project capability."); }
    finally { setBusy(null); }
  }

  async function saveRevision() {
    if (!editProject) return;
    setBusy(`rev:${editProject.project_id}`);
    try {
      await invoke("save_revision", {
        project_id: editProject.project_id,
        requirements: asJson(requirements, []),
        provider_preferences: asJson(providerPreferences, {}),
        resource_preferences: asJson(resourcePreferences, {}),
        policy_constraints: asJson(policyConstraints, {}),
      });
      setNotice("New Infrastructure Project revision saved. Provider infrastructure was not changed.");
      setEditProject(null);
      await refresh();
    } catch (error) { setNotice(error instanceof Error ? error.message : "Revision save failed."); }
    finally { setBusy(null); }
  }

  async function assign() {
    if (!assignProject) return;
    setBusy(`assign:${assignProject.project_id}`);
    try {
      await invoke("assign", {
        project_id: assignProject.project_id,
        application_entity_key: assignApplication === "UNASSIGNED" ? null : assignApplication,
      });
      setNotice(assignApplication === "UNASSIGNED" ? "Project unassigned from the application." : "Project assigned to the application. No provider mutation was performed.");
      setAssignProject(null);
      await refresh();
    } catch (error) { setNotice(error instanceof Error ? error.message : "Assignment failed."); }
    finally { setBusy(null); }
  }


  async function mutateProjectResource(project: Project, item: ProjectResourcePreference, action: "ACTIVE" | "STANDBY" | "DISABLED" | "REMOVE") {
    setBusy(`project-resource:${item.item_key}:${action}`);
    try {
      await invoke(action === "REMOVE" ? "remove_resource_preference" : "set_resource_preference_state", {
        project_id: project.project_id,
        item_key: item.item_key,
        new_state: action === "REMOVE" ? null : action,
        reason: action === "REMOVE"
          ? "Owner removed infrastructure from the project configuration. Provider resource retained."
          : `Owner changed project infrastructure state to ${action}.`,
      });
      const fresh = await refresh();
      if (editProject?.project_id === project.project_id) {
        const next = fresh?.projects.find((candidate) => candidate.project_id === project.project_id);
        if (next) {
          setEditProject(next);
          setResourcePreferences(JSON.stringify(next.latest_revision?.resource_preferences ?? {}, null, 2));
        }
      }
      setNotice(action === "REMOVE" ? "Resource unassigned from project; provider resource retained." : `Project resource changed to ${action}.`);
    } catch (error) { setNotice(error instanceof Error ? error.message : "Project infrastructure update failed."); }
    finally { setBusy(null); }
  }

  async function addSelectedInfrastructure(project: Project) {
    if (!selectionContext || selectionContext.target_scope !== "PROJECT") return;
    const targetEntityKey = selectionContext.provider_capability_key
      ? `PROVIDER_CAPABILITY:${selectionContext.provider_capability_key}`
      : selectionContext.target_entity_key
        ? (selectionContext.target_entity_key.includes(":") ? selectionContext.target_entity_key : `RESOURCE:${selectionContext.target_entity_key}`)
        : null;
    if (!targetEntityKey) return;
    const categoryKey = selectionContext.category_key || "FUTURE_RESOURCE";
    const env = selectionContext.environment_key || project.environment_key || "PRODUCTION";
    const itemKey = `SCOPED:${targetEntityKey}:${env}:PRIMARY`;
    setBusy(`scoped-project:${project.project_id}`);
    try {
      await invoke("add_resource_preference",{
        project_id:project.project_id,
        item_key:itemKey,
        item:{
          item_key:itemKey,
          target_entity_key:targetEntityKey,
          display_name:selectionContext.source_display_name || targetEntityKey,
          category_key:categoryKey,
          provider_key:selectionContext.provider_key ?? null,
          assignment_role:"PRIMARY",
          environment_key:env,
          state:"DESIRED",
          target_kind:selectionContext.provider_capability_key ? "CAPABILITY" : "INSTALLED",
          logical_capability:selectionContext.logical_capability ?? null,
        },
        reason:"Owner added infrastructure from the scoped Applications & Projects workspace.",
      });
      setNotice(`${selectionContext.source_display_name || "Infrastructure"} added to ${project.display_name}. Provider infrastructure was not mutated.`);
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not add selected infrastructure to the project.");
    } finally {
      setBusy(null);
    }
  }

  async function lifecycle(action: "archive" | "restore" | "remove", project: Project) {
    const label = action === "remove" ? "remove from RUNYI" : action;
    if (!window.confirm(`${label} “${project.display_name}”? This changes RUNYI project metadata only and does not destroy provider resources.`)) return;
    setBusy(`${action}:${project.project_id}`);
    try {
      await invoke(action, { project_id: project.project_id });
      setNotice(`${project.display_name}: ${label} completed. Provider resources were untouched.`);
      await refresh();
    } catch (error) { setNotice(error instanceof Error ? error.message : `${label} failed.`); }
    finally { setBusy(null); }
  }

  async function prepareDestroy(resource: ProviderTruth) {
    if (!window.confirm(`Prepare protected destruction for ${resource.provider_resource_name || resource.resource_key}? This does NOT destroy the provider resource; it only verifies whether a protected destructive action is registered.`)) return;
    setBusy(`destroy:${resource.resource_key}`);
    try {
      const data = await invoke("prepare_destroy_provider_resource", { project_id: expanded, resource_key: resource.resource_key });
      setNotice(`${data.status}: ${data.action_key}. Execution remains behind Owner approval/provider-operation controls.`);
    } catch (error) { setNotice(error instanceof Error ? error.message : "Destructive action preparation failed."); }
    finally { setBusy(null); }
  }

  if (!open) return null;

  return (
    <div className={embedded ? "flex min-h-[calc(100dvh-11rem)] w-full flex-col bg-background" : "fixed inset-0 z-[90] bg-background/95 backdrop-blur-sm"}>
      <div className={embedded ? "flex min-h-0 flex-1 flex-col" : "mx-auto flex h-full max-w-7xl flex-col"}>
        <header className="flex items-center gap-2 border-b border-foreground/10 px-3 py-2.5 sm:px-5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5"><Database className="size-4" /><h1 className="truncate text-sm font-semibold">Projects · Application Development</h1><Badge className="h-5 bg-primary/[0.08] px-1.5 text-[9px] text-primary">Platform v{overview?.platform_architecture_version ?? "1.28.0"}</Badge><Badge className="h-5 bg-foreground/[0.06] px-1.5 text-[9px] text-foreground/60">Projects v{overview?.workspace_version ?? "1.27.0"}</Badge></div>
            <p className="mt-0.5 text-[10px] leading-4 text-foreground/45">Application/project configuration, infrastructure assignments and reusable development templates.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => void refresh()} disabled={loading}>{loading ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}</Button>
          {!embedded && <Button variant="ghost" size="icon" onClick={onClose}><X className="size-4" /></Button>}
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-3 sm:p-5">
          <div className="mb-3 flex flex-wrap gap-2">
            {!hideSearch && <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/35" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects & infrastructure…" className="pl-9" /></div>}
            <Button onClick={() => { resetCreate(); setShowCreate(true); }}><Plus className="mr-1.5 size-4" />Create project</Button>
          </div>
          {selectionContext?.target_scope === "PROJECT" && (selectionContext.target_entity_key || selectionContext.provider_capability_key) && (
            <div className="mb-3 rounded-xl border border-primary/20 bg-primary/[0.055] px-3 py-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-primary">Infrastructure selected</p>
              <p className="mt-1 text-xs font-semibold">{selectionContext.source_display_name || selectionContext.target_entity_key || selectionContext.provider_capability_key}</p>
              <p className="mt-1 text-[10px] text-foreground/45">Choose a Project below. This selection remains active until Back returns to Marketplace.</p>
            </div>
          )}

          {notice && <div className="mb-3 rounded-xl border border-foreground/10 bg-foreground/[0.025] px-3 py-2 text-xs text-foreground/70">{notice}</div>}


          {showCreate && (
            <Modal title="New Application / Project Configuration" onClose={() => { setShowCreate(false); resetCreate(); }}>
              <div className="space-y-3 pb-2">
                <section className="rounded-2xl border border-foreground/10 bg-background p-3">
                  <div className="mb-2"><p className="text-xs font-semibold">Project identity and application requirements</p><p className="mt-0.5 text-[10px] text-foreground/45">Configure a reusable project specification before personal ChatGPT, RUNYI AI, agents or developers build it. Link an existing application now or later.</p></div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input value={name} onChange={(e) => { setName(e.target.value); if (!key) setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "")); }} placeholder="Project name" className="h-11 border-foreground/15 bg-background" />
                    <Input value={key} onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9:_-]/g, "_"))} placeholder="PROJECT_KEY" className="h-11 border-foreground/15 bg-background font-mono" />
                  </div>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the app, intended users, requirements and development goals…" className="mt-2 min-h-20 border-foreground/15 bg-background" />
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <Select value={environment} onValueChange={(value) => { setEnvironment(value); syncResourcePreference(resourceChoice, value, assignmentRole); }}><SelectTrigger className="h-11 border-foreground/15 bg-background"><SelectValue /></SelectTrigger><SelectContent className="z-[220]" position="popper">{["DEV","STAGING","CANARY","PRODUCTION"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select>
                    <Select value={application} onValueChange={setApplication}><SelectTrigger className="h-11 border-foreground/15 bg-background"><SelectValue placeholder="No application yet" /></SelectTrigger><SelectContent className="z-[220]" position="popper"><SelectItem value="UNASSIGNED">Unassigned — plan first</SelectItem>{(overview?.applications ?? []).map((item) => <SelectItem key={item.entity_key} value={item.entity_key}>{item.display_name}</SelectItem>)}</SelectContent></Select>
                  </div>
                </section>

                <section className="rounded-2xl border border-foreground/10 bg-background p-3">
                  <div className="mb-2 flex items-center justify-between gap-2"><div><p className="text-xs font-semibold">Infrastructure plan</p><p className="mt-0.5 text-[10px] text-foreground/45">Add requirements and optionally choose a Marketplace capability. Provider resources are not provisioned by this step.</p></div><Badge className="bg-foreground/[0.06] text-foreground/60">{requirementItems.length} req</Badge></div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Select value={requirementCategory} onValueChange={(value) => { setRequirementCategory(value); if (value !== "ANY" && !(overview?.marketplace ?? []).some((item) => item.target_entity_key === resourceChoice && item.category_key === value)) syncResourcePreference("AUTO"); }}><SelectTrigger className="h-11 border-foreground/15 bg-background"><SelectValue placeholder="Infrastructure category" /></SelectTrigger><SelectContent className="z-[220]" position="popper"><SelectItem value="ANY">Any infrastructure category</SelectItem>{(overview?.categories ?? []).map((item) => <SelectItem key={item.category_key} value={item.category_key}>{item.display_name}</SelectItem>)}</SelectContent></Select>
                    <Select value={resourceChoice} onValueChange={(value) => syncResourcePreference(value)}><SelectTrigger className="h-11 border-foreground/15 bg-background"><SelectValue placeholder="Provider / capability" /></SelectTrigger><SelectContent className="z-[220]" position="popper"><SelectItem value="AUTO">Automatic provider resolution</SelectItem>{composerMarketplace.map((item) => <SelectItem key={item.target_entity_key} value={item.target_entity_key}>{item.provider_key} · {item.display_name}</SelectItem>)}</SelectContent></Select>
                    <Select value={assignmentRole} onValueChange={(value) => { setAssignmentRole(value); syncResourcePreference(resourceChoice, environment, value); }}><SelectTrigger className="h-11 border-foreground/15 bg-background"><SelectValue /></SelectTrigger><SelectContent className="z-[220]" position="popper">{["PRIMARY","SECONDARY","BACKUP"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select>
                    <Select value={requirementPriority} onValueChange={setRequirementPriority}><SelectTrigger className="h-11 border-foreground/15 bg-background"><SelectValue /></SelectTrigger><SelectContent className="z-[220]" position="popper">{["REQUIRED","PREFERRED","OPTIONAL"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div className="mt-2 flex gap-2"><Input value={requirementText} onChange={(e) => setRequirementText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStructuredRequirement(); } }} placeholder="Add a requirement, e.g. PostgreSQL with vector search" className="h-11 border-foreground/15 bg-background" /><Button variant="outline" className="h-11 shrink-0" onClick={addStructuredRequirement} disabled={!requirementText.trim()}><Plus className="mr-1 size-4" />Add</Button></div>
                  {(asJson(resourcePreferences, {}) as {items?: ProjectResourcePreference[]}).items?.length ? <div className="mt-2 rounded-xl border border-primary/20 p-2"><p className="mb-2 text-[10px] font-semibold">Selected Marketplace infrastructure</p>{((asJson(resourcePreferences, {}) as {items?: ProjectResourcePreference[]}).items ?? []).map((item) => <div key={item.item_key} className="mb-1 flex items-center gap-2 rounded-lg bg-foreground/[0.025] p-2"><div className="min-w-0 flex-1"><p className="truncate text-[10px] font-semibold">{item.display_name ?? item.target_entity_key}</p><p className="text-[9px] text-foreground/45">{item.provider_key} · {item.category_key} · {item.environment_key} · {item.assignment_role}</p></div><Button size="icon" variant="ghost" className="size-7" aria-label={`Remove ${item.display_name ?? item.target_entity_key} from draft`} onClick={() => removeCreateResourcePreference(item.item_key)}><X className="size-3.5" /></Button></div>)}</div> : null}
                  {requirementItems.length > 0 && <div className="mt-2 grid gap-1.5">{requirementItems.map((item, index) => <div key={`${index}:${String(item.requirement ?? "requirement")}`} className="flex items-start gap-2 rounded-xl border border-foreground/8 bg-foreground/[0.02] px-2.5 py-2"><div className="min-w-0 flex-1"><p className="text-[10px] font-medium">{String(item.requirement ?? "Infrastructure requirement")}</p><p className="mt-0.5 text-[9px] text-foreground/40">{String(item.category_key ?? "FUTURE_RESOURCE")} · {String(item.priority ?? "REQUIRED")}</p></div><Button variant="ghost" size="icon" className="size-7 shrink-0" onClick={() => removeStructuredRequirement(index)} title="Remove requirement"><X className="size-3.5" /></Button></div>)}</div>}
                </section>

                <button type="button" className="flex w-full items-center justify-between rounded-xl border border-foreground/10 bg-background px-3 py-2.5 text-left" onClick={() => setShowAdvancedCreate((value) => !value)}><span><span className="block text-[10px] font-semibold">Advanced configuration</span><span className="mt-0.5 block text-[9px] text-foreground/40">Optional JSON for specialized requirements, provider/resource preferences and policy constraints.</span></span><ChevronDown className={`size-4 shrink-0 text-foreground/45 transition ${showAdvancedCreate ? "rotate-180" : ""}`} /></button>
                {showAdvancedCreate && <div className="grid gap-2 lg:grid-cols-2"><JsonBox label="Requirements JSON" value={requirements} onChange={setRequirements} /><JsonBox label="Provider preferences JSON" value={providerPreferences} onChange={setProviderPreferences} /><JsonBox label="Resource preferences JSON" value={resourcePreferences} onChange={setResourcePreferences} /><JsonBox label="Policy constraints JSON" value={policyConstraints} onChange={setPolicyConstraints} /></div>}

                <div className="sticky bottom-0 z-10 -mx-1 flex justify-end gap-2 border-t border-foreground/10 bg-background/95 px-1 pb-[max(0.25rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur"><Button variant="outline" onClick={() => { setShowCreate(false); resetCreate(); }}>Cancel</Button><Button onClick={() => void createProject()} disabled={busy === "create" || !name.trim() || !key.trim()}>{busy === "create" ? <LoaderCircle className="mr-1.5 size-4 animate-spin" /> : <Plus className="mr-1.5 size-4" />}Create project</Button></div>
              </div>
            </Modal>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto pr-0.5">
            <div className="grid gap-2 lg:grid-cols-2">
              {filteredProjects.map((project) => {
                const isExpanded = expanded === project.project_id;
                const appName = project.application_entity_key ? applicationMap.get(project.application_entity_key) ?? project.application_entity_key : "Unassigned";
                const configuredItems = projectResourceItems(project);
                return <Card key={project.project_id} className="border-foreground/8 bg-card py-0 shadow-sm"><CardContent className="p-3">
                  <div className="flex items-start gap-1">
                    <button className="flex min-w-0 flex-1 items-start gap-2 text-left" onClick={() => setExpanded(isExpanded ? null : project.project_id)}>
                      <Boxes className="mt-0.5 size-4 shrink-0 text-foreground/45" /><div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{project.display_name}</p><p className="mt-0.5 truncate font-mono text-[9px] text-foreground/40">{project.project_key}</p></div><div className="flex max-w-[52%] shrink-0 flex-wrap justify-end gap-1"><Badge className={tone(project.lifecycle_state)}>{project.lifecycle_state}</Badge><Badge className={tone(project.assignment_state)}>{project.assignment_state}</Badge><ChevronDown className={`mt-1 size-4 text-foreground/40 transition ${isExpanded ? "rotate-180" : ""}`} /></div>
                    </button>
                    {selectionContext?.target_scope === "PROJECT" && (selectionContext.target_entity_key || selectionContext.provider_capability_key) && (
                      <Button variant="outline" size="sm" className="h-8 shrink-0 rounded-lg px-2 text-[9px]" disabled={busy === `scoped-project:${project.project_id}`} onClick={() => void addSelectedInfrastructure(project)}>
                        {busy === `scoped-project:${project.project_id}` ? <LoaderCircle className="size-3 animate-spin" /> : <Plus className="size-3" />}Add
                      </Button>
                    )}
                    <ProjectOperatingMenu
                      project={project}
                      onConfigure={() => startEdit(project)}
                      onOpenTest={() => onOpenTest?.(project)}
                      onAssign={() => { setAssignProject(project); setAssignApplication(project.application_entity_key ?? "UNASSIGNED"); }}
                    />
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-1.5"><Mini label="Environment" value={project.environment_key} /><Mini label="Revision" value={`r${project.current_revision}`} /><Mini label="Application" value={appName} /></div>
                  {isExpanded && <div className="mt-3 border-t border-foreground/8 pt-3">
                    <p className="mb-2 text-xs text-foreground/55">{project.description || "No description."}</p>
                    <div className="mb-3 space-y-1.5 rounded-xl border border-foreground/10 p-2.5"><p className="text-[10px] font-semibold">Project AI · Provider & model</p><UniversalAiModelPicker supabase={supabase ?? fallbackSupabase} connected={connected ?? true} projectId={project.project_id} environmentKey={project.environment_key} /></div>
                    <div className="mb-3 rounded-xl border border-foreground/8 bg-foreground/[0.02] p-2.5">
                      <div className="mb-2 flex items-center justify-between gap-2"><p className="text-[10px] font-semibold">Configured infrastructure</p><Badge className="bg-foreground/[0.05] text-foreground/55">{configuredItems.length}</Badge></div>
                      {configuredItems.length === 0 ? <p className="text-[10px] text-foreground/42">No infrastructure selected yet. Use the Marketplace below or any infrastructure ⋮ menu → Add to application or project.</p> : <div className="grid gap-1.5 sm:grid-cols-2">{configuredItems.map((item) => <div key={item.item_key} className="rounded-lg bg-background/70 p-2"><div className="flex items-start justify-between gap-2"><div className="min-w-0"><p className="truncate text-[10px] font-semibold">{item.display_name || item.target_entity_key}</p><p className="mt-0.5 text-[8px] text-foreground/42">{item.environment_key || project.environment_key} · {item.category_key} · {item.assignment_role || "PRIMARY"}</p></div><div className="flex shrink-0 items-start gap-1"><Badge className={tone(item.state)}>{item.state || "STANDBY"}</Badge><ProjectResourceActions state={item.state || "STANDBY"} onActive={() => void mutateProjectResource(project,item,"ACTIVE")} onStandby={() => void mutateProjectResource(project,item,"STANDBY")} onDisable={() => void mutateProjectResource(project,item,"DISABLED")} onRemove={() => void mutateProjectResource(project,item,"REMOVE")} /></div></div><p className="mt-1 break-all font-mono text-[8px] text-foreground/35">{item.target_entity_key}</p></div>)}</div>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => startEdit(project)} disabled={project.lifecycle_state === "ARCHIVED" || project.lifecycle_state === "REMOVED"}>Edit Configuration</Button>
                      <Button size="sm" variant="outline" onClick={() => { setAssignProject(project); setAssignApplication(project.application_entity_key ?? "UNASSIGNED"); }} disabled={project.assignment_state === "MATERIALIZED" || project.lifecycle_state === "REMOVED"}>Assign application</Button>
                      {project.lifecycle_state === "ARCHIVED" ? <Button size="sm" variant="outline" onClick={() => void lifecycle("restore", project)}><RotateCcw className="mr-1 size-3.5" />Restore</Button> : <Button size="sm" variant="outline" onClick={() => void lifecycle("archive", project)} disabled={project.lifecycle_state === "REMOVED"}><Archive className="mr-1 size-3.5" />Archive</Button>}
                      <Button size="sm" variant="outline" onClick={() => void lifecycle("remove", project)} disabled={project.lifecycle_state === "REMOVED"}><Trash2 className="mr-1 size-3.5" />Remove from RUNYI</Button>
                    </div>
                    <div className="mt-3 rounded-xl bg-foreground/[0.025] p-2.5 text-[10px] text-foreground/50"><strong className="text-foreground/65">Lifecycle safety:</strong> Archive/Remove change RUNYI metadata only. Provider resources are untouched. Provider destruction is a separate protected action.</div>
                  </div>}
                </CardContent></Card>;
              })}
              {!loading && filteredProjects.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-foreground/12 p-8 text-center text-sm text-foreground/45">No projects match this view. Create an application project and select its infrastructure.</div>}
            </div>

            {!onlyApplicationEntityKey && <section className="mt-5">
              <div className="mb-2 flex items-center justify-between gap-2"><div><div className="flex items-center gap-2"><Boxes className="size-4" /><h2 className="text-sm font-semibold">Universal Infrastructure Marketplace</h2></div><p className="mt-1 text-[10px] text-foreground/45">Add any capability to an Infrastructure Project or application. Multiple alternatives may be retained; one ACTIVE per environment/category/role.</p></div><Badge className="bg-foreground/[0.05] text-foreground/55">{filteredMarketplace.length}</Badge></div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{filteredMarketplace.map((item) => <Card key={item.provider_capability_key} className="border-foreground/8 bg-card py-0"><CardContent className="p-3"><div className="flex items-start justify-between gap-2"><div className="min-w-0"><p className="truncate text-xs font-semibold">{item.display_name}</p><p className="mt-0.5 text-[9px] uppercase tracking-wider text-foreground/40">{item.category_key.replaceAll("_"," ")}</p></div><UniversalInfrastructureActions supabase={supabase} connected={connected} applications={overview?.applications ?? []} target={{ target_entity_key: item.target_entity_key, display_name: item.display_name, category_key: item.category_key, provider_key: item.provider_key, logical_capability: item.logical_capability_hint ?? null, target_kind: "CAPABILITY" }} onChanged={() => void refresh()} /></div><div className="mt-2 flex flex-wrap gap-1"><Badge className="bg-foreground/[0.05] text-foreground/55">{item.provider_key}</Badge><Badge className={tone(item.state)}>{item.state}</Badge></div><p className="mt-2 break-all font-mono text-[8px] text-foreground/35">{item.provider_capability_key}</p></CardContent></Card>)}</div>
            </section>}

            {!onlyApplicationEntityKey && <section className="mt-5">
              <div className="mb-2 flex items-center gap-2"><HardDrive className="size-4" /><h2 className="text-sm font-semibold">Verified provider resource identity</h2></div>
              <p className="mb-3 text-[10px] text-foreground/45">Provider account/project identity is shown only when verified. Unknown values remain “Unassigned / Not connected”.</p>
              <div className="grid gap-2 xl:grid-cols-2">{(overview?.provider_truth ?? []).map((resource) => <Card key={resource.binding_id} className="border-foreground/8 bg-card py-0"><CardContent className="p-3">
                <div className="flex flex-wrap items-start gap-2"><Database className="mt-0.5 size-4 shrink-0 text-foreground/45" /><div className="min-w-[160px] flex-1"><p className="truncate text-xs font-semibold">{resource.provider_resource_name || resource.provider_resource_id}</p><p className="mt-0.5 break-all font-mono text-[9px] text-foreground/40">{resource.resource_key}</p></div><div className="flex shrink-0 items-start gap-1"><Badge className={tone(resource.verification_state)}>{resource.verification_state}</Badge><UniversalInfrastructureActions supabase={supabase} connected={connected} applications={overview?.applications ?? []} target={{ target_entity_key: `RESOURCE:${resource.resource_key}`, display_name: resource.provider_resource_name || resource.provider_resource_id, category_key: categoryForProviderResource(resource.provider_resource_type), provider_key: resource.provider_key, target_kind: "PROVIDER_RESOURCE" }} onChanged={() => void refresh()} /></div></div>
                <div className="mt-2 grid gap-1.5 sm:grid-cols-2"><Mini label="Provider" value={resource.provider_key} /><Mini label="Type" value={resource.provider_resource_type} /><Mini label="Account" value={resource.provider_account_display} /><Mini label="Provider project" value={resource.provider_project_display} /></div>
                <div className="mt-2 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between"><p className="break-words text-[9px] text-foreground/40">Binding: {resource.provider_account_ref || resource.provider_project_ref ? "Connected / verified identity" : "Unassigned / Not connected"} · Destruction: {resource.destroy_supported ? `${resource.destroy_action_key} · ${resource.destroy_action?.risk_level ?? "protected"}` : "No registered destructive action"}</p><Button size="sm" variant="outline" className="w-full sm:w-auto" disabled={!resource.destroy_supported || busy === `destroy:${resource.resource_key}`} onClick={() => void prepareDestroy(resource)}>{busy === `destroy:${resource.resource_key}` ? <LoaderCircle className="mr-1 size-3.5 animate-spin" /> : <ShieldAlert className="mr-1 size-3.5" />}Prepare protected destroy</Button></div>
              </CardContent></Card>)}</div>
            </section>}
          </div>
        </div>
      </div>

      {editProject && <Modal title={`Workspace Configuration Engine · ${editProject.display_name}`} onClose={() => setEditProject(null)}>
        <div className="space-y-3 pb-2">
          <p className="text-xs text-foreground/55">Project ${editProject.project_key} · revision ${liveEditProject()?.current_revision ?? editProject.current_revision}. Change this product's configuration without deleting shared provider infrastructure.</p>
          <section className="rounded-xl border border-foreground/10 p-3"><p className="text-xs font-semibold">Project AI · Provider & model</p><p className="mt-1 text-[10px] text-foreground/50">Use the authorized AI Marketplace resources assigned to this Project.</p><div className="mt-2"><UniversalAiModelPicker supabase={supabase} connected={connected} projectId={editProject.project_id} environmentKey={editProject.environment_key} /></div></section>
          <section className="rounded-xl border border-foreground/10 p-3">
            <p className="text-xs font-semibold">Infrastructure, products & services</p>
            <div className="mt-2 space-y-2">
              {projectResourceItems(liveEditProject() ?? editProject).map((item) => <div key={item.item_key} className="flex items-center justify-between gap-2 rounded-lg bg-foreground/[0.025] p-2"><div className="min-w-0 flex-1"><p className="truncate text-xs font-semibold">{item.display_name ?? item.target_entity_key}</p><p className="text-[10px] text-foreground/50">{item.category_key} · {item.provider_key ?? "Unknown provider"} · {item.state ?? "DESIRED"}</p></div><ProjectResourceActions state={item.state ?? "DESIRED"} onActive={() => void mutateProjectResource(editProject,item,"ACTIVE")} onStandby={() => void mutateProjectResource(editProject,item,"STANDBY")} onDisable={() => void mutateProjectResource(editProject,item,"DISABLED")} onRemove={() => void mutateProjectResource(editProject,item,"REMOVE")} /></div>)}
            </div>
            <div className="mt-3 flex gap-2"><Select value={editResourceTarget || "NONE"} onValueChange={setEditResourceTarget}><SelectTrigger className="min-w-0 flex-1"><SelectValue placeholder="Provider / Marketplace capability" /></SelectTrigger><SelectContent className="z-[220]" position="popper"><SelectItem value="NONE">Choose infrastructure</SelectItem>{(overview?.marketplace ?? []).map((item) => <SelectItem key={item.provider_capability_key} value={item.target_entity_key}>{item.provider_key} · {item.display_name}</SelectItem>)}</SelectContent></Select><Button variant="outline" size="sm" disabled={!editResourceTarget || editResourceTarget === "NONE" || busy?.startsWith("edit-add:")} onClick={() => void addEditResourcePreference()}><Plus className="size-3.5" />Add</Button></div>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => { dispatchAddInfrastructure({ source:"PROJECT_CONFIG_ENGINE", project_id:editProject.project_id, project_display_name:editProject.display_name, target_scope:"PROJECT", environment_key:editProject.environment_key, mode:"NEW" }); setEditProject(null); }}>Browse full Marketplace</Button>
          </section>
          <section className="rounded-xl border border-foreground/10 p-3"><p className="text-xs font-semibold">Project requirements</p><div className="mt-2 flex gap-2"><Input value={requirementText} onChange={(e) => setRequirementText(e.target.value)} placeholder="Application requirement…" /><Button variant="outline" onClick={addStructuredRequirement} disabled={!requirementText.trim()}><Plus className="size-3.5" />Add</Button></div>{requirementItems.map((item,index) => <div key={index} className="mt-1 flex items-center gap-2 rounded-lg bg-foreground/[0.025] p-2"><p className="min-w-0 flex-1 text-[10px]">{String(item.requirement ?? "Requirement")}</p><Button size="icon" variant="ghost" className="size-7" onClick={() => removeStructuredRequirement(index)}><X className="size-3.5" /></Button></div>)}</section>
          <section className="rounded-xl border border-foreground/10 p-3"><p className="text-xs font-semibold">AI & developer infrastructure template</p><p className="mt-1 text-[10px] text-foreground/50">Versioned project specification for authorized ChatGPT, RUNYI AI, agents and developers. Access to provider resources is separately governed.</p><Button size="sm" variant="outline" className="mt-2" onClick={() => setShowProjectTemplate((value) => !value)}>{showProjectTemplate ? "Hide" : "View"} template</Button>{showProjectTemplate && <Textarea readOnly value={projectTemplate(liveEditProject() ?? editProject)} aria-label="Project development template" className="mt-2 min-h-52 font-mono text-[10px]" />}</section>
          <details className="rounded-xl border border-foreground/10 p-3"><summary className="cursor-pointer text-xs font-semibold">Advanced configuration</summary><div className="mt-2 grid gap-2"><JsonBox label="Requirements" value={requirements} onChange={setRequirements} /><JsonBox label="Provider preferences" value={providerPreferences} onChange={setProviderPreferences} /><JsonBox label="Resource preferences" value={resourcePreferences} onChange={setResourcePreferences} /><JsonBox label="Policy constraints" value={policyConstraints} onChange={setPolicyConstraints} /></div></details>
          <div className="sticky bottom-0 flex justify-end gap-2 border-t border-foreground/10 bg-background/95 py-3"><Button variant="outline" onClick={() => setEditProject(null)}>Close</Button><Button onClick={() => void saveRevision()} disabled={busy === `rev:${editProject.project_id}`}>{busy === `rev:${editProject.project_id}` && <LoaderCircle className="mr-1.5 size-4 animate-spin" />}Save configuration revision</Button></div>
        </div>
      </Modal>}

      {assignProject && <Modal title={`Assign application · ${assignProject.display_name}`} onClose={() => setAssignProject(null)}><Select value={assignApplication} onValueChange={setAssignApplication}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent className="z-[220]" position="popper"><SelectItem value="UNASSIGNED">Unassigned — keep independent</SelectItem>{(overview?.applications ?? []).map((item) => <SelectItem key={item.entity_key} value={item.entity_key}>{item.display_name}</SelectItem>)}</SelectContent></Select><p className="mt-2 text-[10px] text-foreground/45">Assignment does not provision, deploy or mutate provider infrastructure. Materialization continues through the existing Composition → Desired State → Flow path.</p><div className="mt-3 flex justify-end gap-2"><Button variant="outline" onClick={() => setAssignProject(null)}>Cancel</Button><Button onClick={() => void assign()} disabled={busy === `assign:${assignProject.project_id}`}>{busy === `assign:${assignProject.project_id}` && <LoaderCircle className="mr-1.5 size-4 animate-spin" />}Apply assignment</Button></div></Modal>}
    </div>
  );
}

function ProjectOperatingMenu({ project,onConfigure,onAssign,onOpenTest }: { project: Project; onConfigure: () => void; onAssign: () => void; onOpenTest: () => void }) {
  return <div className="ml-auto shrink-0" onClick={(event) => event.stopPropagation()}>
    <EnterpriseContextMenu compact label={`Project actions for ${project.display_name}`} title="Project actions">
      <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => {
        dispatchAddInfrastructure({ source:"PROJECT_MENU",project_id:project.project_id,project_display_name:project.display_name,
          environment_key:project.environment_key,target_scope:"PROJECT",mode:"NEW" });
      }}><Plus className="size-3.5" />Add Infrastructure</button>
      <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={onConfigure}>Edit Configuration</button>
      <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={onOpenTest}>Open / Test Project</button>
      <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={onAssign}>Assign application</button>
      <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-xs text-foreground/60 hover:bg-foreground/[0.05]" onClick={() => window.dispatchEvent(new CustomEvent("runyi:project-open",{detail:{projectId:project.project_id}}))}>Open project workspace</button>
    </EnterpriseContextMenu>
  </div>;
}

function ProjectResourceActions({ state,onActive,onStandby,onDisable,onRemove }: { state: string; onActive: () => void; onStandby: () => void; onDisable: () => void; onRemove: () => void }) {
  return <EnterpriseContextMenu compact label="Project resource actions" width={192}>
    {state !== "ACTIVE" && <button className="w-full rounded-lg px-2.5 py-2 text-left text-[10px] hover:bg-foreground/[0.05]" onClick={onActive}>Set active</button>}
    {state === "ACTIVE" && <button className="w-full rounded-lg px-2.5 py-2 text-left text-[10px] hover:bg-foreground/[0.05]" onClick={onStandby}>Deactivate → Standby</button>}
    <button className="w-full rounded-lg px-2.5 py-2 text-left text-[10px] hover:bg-foreground/[0.05]" onClick={onDisable}>Disable</button>
    <button className="w-full rounded-lg px-2.5 py-2 text-left text-[10px] hover:bg-foreground/[0.05]" onClick={onRemove}>Remove from project</button>
  </EnterpriseContextMenu>;
}

function JsonBox({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="block"><span className="mb-1 block text-[9px] font-semibold uppercase tracking-wider text-foreground/45">{label}</span><Textarea value={value} onChange={(e) => onChange(e.target.value)} className="min-h-28 font-mono text-[10px]" spellCheck={false} /></label>;
}

function Mini({ label, value }: { label: string; value: string | number }) {
  return <div className="min-w-0 rounded-lg bg-foreground/[0.025] px-2 py-1.5"><p className="text-[8px] font-semibold uppercase tracking-wider text-foreground/35">{label}</p><p className="mt-0.5 truncate text-[10px] font-medium text-foreground/65" title={String(value)}>{value}</p></div>;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return <div className="fixed inset-0 z-[140] grid place-items-center bg-background/75 p-3 backdrop-blur-sm"><Card className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden border-foreground/12 bg-background py-0 shadow-2xl"><CardContent className="flex min-h-0 flex-1 flex-col p-4"><div className="mb-3 flex shrink-0 items-center gap-2"><h3 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h3><Button variant="ghost" size="icon" onClick={onClose}><X className="size-4" /></Button></div><div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5">{children}</div></CardContent></Card></div>;
}
